import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavigationTabs } from "@/components/common/navigationTabs";
import { TableContainer } from "@/components/common/TableContainer";
import { DataView } from "@/components/common/DataView";
import { ActionButton } from "@/components/common/DataViewTypes";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { useAppDispatch } from "@/store/hooks";
import { Plus, ShoppingCart, RefreshCw, X } from "lucide-react";
import useProducts from "@/hooks/useProducts";
import { useConfirmation } from "@/components/common/ConfirmationDialog";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getColumns } from "./columns";
import { Product } from "@/types/models/product";
import { searchProductsRequest, clearProductSearchResults, fetchProductsRequest } from "@/store/slices/productNew.slice";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { importDummyProducts } from "@/services/productService";

const productTabs = [
  { label: "Active", status: "ACTIVE" },
  { label: "Draft", status: "DRAFT" },
  { label: "Inactive", status: "INACTIVE" },
  { label: "All", status: null },
];

export default function Products() {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { confirm } = useConfirmation();
  const dispatch = useAppDispatch();

  const currentStoreInfo = useSelector((state: RootState) => state.auth.currentStoreInfo);

  // Get both listing and search state from Redux
  const {
    items,
    loading,
    error,
    pagination,
    fetchItems,
  } = useProducts();

  const searchResults = useSelector((state: RootState) => state.products.searchResults);
  const searchLoading = useSelector((state: RootState) => state.products.searchLoading);
  const searchError = useSelector((state: RootState) => state.products.searchError);
  const searchPagination = useSelector((state: RootState) => state.products.searchPagination);

  const initialStatus = searchParams.get("status");
  const initialCollection = searchParams.get("collection") || "";
  const initialPage = Number(searchParams.get("page") || "1");
  const [pageIndex, setPageIndex] = useState<number>(Number.isFinite(initialPage) && initialPage > 0 ? initialPage - 1 : 0);
  const [activeTab, setActiveTab] = useState<string | null>(initialStatus ? initialStatus : (initialCollection ? null : "ACTIVE"));
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get("q") || "");
  const [collectionFilter, setCollectionFilter] = useState<string>(initialCollection);
  const [sortBy, setSortBy] = useState<string>(searchParams.get("sortBy") || "updated_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">((searchParams.get("sortOrder") as "asc" | "desc") || "desc");
  const [isImportingDummyData, setIsImportingDummyData] = useState(false);
  const pageSize = 10;
  const lastRequestKeyRef = useRef<string>("");

  const isSeller = currentStoreInfo?.storeType === "seller";

  // Create columns with current store ID
  const columns = useMemo(() => {
    return getColumns(currentStoreInfo?.storeId);
  }, [currentStoreInfo?.storeId]);

  const fetchProducts = useCallback(() => {
    const hasSearch = searchQuery.trim();
    const filters: Record<string, any> = {};
    if (activeTab) filters.status = activeTab;
    if (collectionFilter) filters.collection_id = collectionFilter;

    if (hasSearch) {
      // Use search API when there's a search query
      const searchParams: any = {
        query: searchQuery,
        page: pageIndex + 1,
        pageSize: pageSize,
        sortBy,
        sortOrder,
        filters,
      };

      dispatch(searchProductsRequest(searchParams));
    } else {
      // Clear search results when switching back to listing
      dispatch(clearProductSearchResults());

      // Use listing API when there's no search query
      const requestParams: any = {
        page: pageIndex + 1,
        pageSize: pageSize,
        sortBy,
        sortOrder,
        filters,
      };

      fetchItems(requestParams);
    }
  }, [searchQuery, pageIndex, activeTab, collectionFilter, sortBy, sortOrder, fetchItems, dispatch]);

  // Use search state when searching, otherwise use listing state
  const hasSearch = searchQuery.trim();
  const currentData = hasSearch ? (searchResults || []) : (items || []);
  const currentPagination = hasSearch
    ? {
        totalPages: searchPagination?.totalPages || 1,
        currentPage: searchPagination?.page || 1,
        totalItems: searchPagination?.total || 0,
        pageSize: searchPagination?.pageSize || 10,
        total: searchPagination?.total || 0,
      }
    : {
        totalPages: pagination?.totalPages || 1,
        currentPage: (pagination?.page || 0) + 1,
        totalItems: pagination?.total || 0,
        pageSize: pagination?.pageSize || 10,
        total: pagination?.total || 0,
      };
  const currentLoading = hasSearch ? searchLoading : loading;
  const currentError = hasSearch ? searchError : error;

  // Keep URL in sync so routing into this page can preset filters/sort/page
  useEffect(() => {
    const next = new URLSearchParams();
    if (activeTab) next.set("status", activeTab);
    if (searchQuery.trim()) next.set("q", searchQuery.trim());
    if (collectionFilter) next.set("collection", collectionFilter);
    if (sortBy) next.set("sortBy", sortBy);
    if (sortOrder) next.set("sortOrder", sortOrder);
    if (pageIndex > 0) next.set("page", String(pageIndex + 1));
    setSearchParams(next, { replace: true });
  }, [activeTab, searchQuery, collectionFilter, sortBy, sortOrder, pageIndex, setSearchParams]);

  // If URL is changed externally (e.g. from collections page), hydrate UI state from it.
  useEffect(() => {
    const urlStatus = searchParams.get("status");
    const urlQuery = searchParams.get("q") || "";
    const urlCollection = searchParams.get("collection") || "";
    const urlSortBy = searchParams.get("sortBy") || "updated_at";
    const urlSortOrder = (searchParams.get("sortOrder") as "asc" | "desc") || "desc";
    const urlPage = Number(searchParams.get("page") || "1");

    setActiveTab(urlStatus || null);
    setSearchQuery(urlQuery);
    setCollectionFilter(urlCollection);
    setSortBy(urlSortBy);
    setSortOrder(urlSortOrder);
    setPageIndex(Number.isFinite(urlPage) && urlPage > 0 ? urlPage - 1 : 0);
  }, [searchParams]);

  useEffect(() => {
    const requestKey = JSON.stringify({
      mode: searchQuery.trim() ? "search" : "list",
      page: pageIndex,
      status: activeTab,
      q: searchQuery.trim(),
      collection: collectionFilter,
      sortBy,
      sortOrder,
    });
    const shouldFetch = currentData.length === 0 || lastRequestKeyRef.current !== requestKey;
    if (shouldFetch) {
      fetchProducts();
      lastRequestKeyRef.current = requestKey;
    }
  }, [pageIndex, activeTab, searchQuery, collectionFilter, sortBy, sortOrder, currentData.length, fetchProducts]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPageIndex(0);
  };

  const handleTabChange = (status: string | null) => {
    setActiveTab(status);
    setPageIndex(0);
  };

  const clearCollectionFilter = () => {
    setCollectionFilter("");
    setPageIndex(0);
  };

  const clearStatusFilter = () => {
    setActiveTab(null);
    setPageIndex(0);
  };

  const handleRowClick = (product: Product) => {
    // Don't allow editing imported products
    if (product.owner_id !== currentStoreInfo?.storeId) return;
    
    if (!params) return;
    const storeType = params.storeType as string;
    const storeId = params.storeId as string;
    navigate(`/${storeType}/${storeId}/product/create?q=${product.id}`);
  };

  const handlePageChange = (newPageIndex: number) => setPageIndex(newPageIndex);

  const getStatusTitle = () => {
    if (!activeTab) return "All Products";
    switch (activeTab) {
      case "ACTIVE":
        return "Active Products";
      case "DRAFT":
        return "Draft Products";
      case "INACTIVE":
        return "Inactive Products";
      default:
        return "Products";
    }
  };

  const getStatusDescription = () => {
    if (!activeTab) return "Manage your product catalog, inventory, and pricing";
    switch (activeTab) {
      case "ACTIVE":
        return "Products currently available for sale";
      case "DRAFT":
        return "Products in development or awaiting approval";
      case "INACTIVE":
        return "Products temporarily unavailable";
      default:
        return "Manage your product catalog";
    }
  };

  const handleAddProduct = () => {
    if (!params) return;
    const storeType = params.storeType as string;
    const storeId = params.storeId as string;
    navigate(`/${storeType}/${storeId}/product/create`);
  };

  const handleAddFromWholesaler = () => {
    if (!params) return;
    const storeType = params.storeType as string;
    const storeId = params.storeId as string;
    navigate(`/${storeType}/${storeId}/product/wholesaler`);
  };

  const handleAddDummyData = async () => {
    if (isImportingDummyData) return;

    try {
      setIsImportingDummyData(true);
      await importDummyProducts();
      toast.success("Dummy products imported successfully");
      handleRefresh();
    } catch (err: any) {
      toast.error(err?.message || "Failed to import dummy products");
    } finally {
      setIsImportingDummyData(false);
    }
  };

  // Handle refresh - directly dispatch action to force a fresh fetch
  const handleRefresh = () => {
    const hasSearchQuery = searchQuery.trim();
    const filters: Record<string, any> = {};
    if (activeTab) filters.status = activeTab;
    if (collectionFilter) filters.collection_id = collectionFilter;

    if (hasSearchQuery) {
      const searchParams: any = {
        query: searchQuery,
        page: pageIndex + 1,
        pageSize: pageSize,
        sortBy,
        sortOrder,
        filters,
      };
      dispatch(searchProductsRequest(searchParams));
    } else {
      dispatch(clearProductSearchResults());
      const requestParams: any = {
        page: pageIndex + 1,
        pageSize: pageSize,
        sortBy,
        sortOrder,
        filters,
      };
      dispatch(fetchProductsRequest(requestParams));
    }
  };

  const activeFilterChips = [
    collectionFilter
      ? {
          key: "collection",
          label: `Collection: ${collectionFilter}`,
          onRemove: clearCollectionFilter,
        }
      : null,
    activeTab
      ? {
          key: "status",
          label: `Status: ${activeTab}`,
          onRemove: clearStatusFilter,
        }
      : null,
  ].filter(Boolean) as Array<{ key: string; label: string; onRemove: () => void }>;

  const actionButtons: ActionButton[] = [
    {
      label: "Refresh",
      icon: <RefreshCw className={`h-3 w-3 ${currentLoading ? 'animate-spin' : ''}`} />,
      onClick: handleRefresh,
      variant: "outline",
      disabled: currentLoading,
    },
    {
      label: "Add Product",
      icon: <Plus className="h-3 w-3" />,
      onClick: handleAddProduct,
      variant: "primary",
    },
    {
      label: "Add Dummy Data",
      icon: <Plus className={`h-3 w-3 ${isImportingDummyData ? "animate-spin" : ""}`} />,
      onClick: handleAddDummyData,
      variant: "outline",
      disabled: isImportingDummyData,
    },
  ];

  if (isSeller) {
    actionButtons.push({
      label: "Add from Wholesaler",
      icon: <ShoppingCart className="h-3 w-3" />,
      onClick: handleAddFromWholesaler,
      variant: "outline",
    });
  }

  return (
    <div className="space-y-4">
      <TableContainer
        navigationTabs={
          <NavigationTabs
            tabs={productTabs.map((tab) => ({
              label: tab.label,
              onClick: () => handleTabChange(tab.status),
              isActive: activeTab === tab.status,
            }))}
            hasBackButton={false}
          />
        }
      >
        <div className="bg-secondary-background p-[18px] border rounded-[16px]">
          <div className="flex flex-col gap-3 mb-3">
            <div className="flex flex-wrap items-center gap-2">
              {activeFilterChips.map((chip) => (
                <Badge key={chip.key} variant="secondary" className="rounded-full pl-3 pr-2 py-1 text-xs gap-1">
                  {chip.label}
                  <button
                    type="button"
                    onClick={chip.onRemove}
                    className="inline-flex items-center justify-center rounded-full hover:bg-black/10 p-0.5"
                    aria-label={`Remove ${chip.label} filter`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setPageIndex(0);
                }}
                className="h-8 text-xs rounded-full border px-3 bg-background"
              >
                <option value="updated_at">Sort by Updated</option>
                <option value="created_at">Sort by Created</option>
                <option value="title">Sort by Title</option>
                <option value="price">Sort by Price</option>
              </select>
              <select
                value={sortOrder}
                onChange={(e) => {
                  setSortOrder(e.target.value as "asc" | "desc");
                  setPageIndex(0);
                }}
                className="h-8 text-xs rounded-full border px-3 bg-background"
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>
          </div>
          <DataView
            columns={columns}
            data={currentData}
            pagination={currentPagination}
            pageIndex={pageIndex}
            onPageChange={handlePageChange}
            loading={currentLoading || false}
            error={currentError?.message || null}
            title={getStatusTitle()}
            description={getStatusDescription()}
            searchPlaceholder="Search products..."
            searchQuery={searchQuery}
            onSearch={handleSearch}
            actionButtons={actionButtons}
            onRowClick={handleRowClick}
            enableColumnResizing={true}
            enableRowSelection={false}
            emptyStateType="products"
            loadingMessage="Loading..."
          />
        </div>
      </TableContainer>
    </div>
  );
}
